import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Pagination.css';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  scrollTargetSelector = null,
  scrollOffset = 80
}) => {
  const [jumpPage, setJumpPage] = useState('');
  const [showError, setShowError] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);

  // 计算可见页码（不包括第一页和最后一页）
  const getVisiblePages = useCallback((total, current) => {
    const maxButtons = 5; // 最多显示的页码按钮数（不包括上一页/下一页/跳转）

    // 如果总页数较少，直接显示所有页码
    if (total <= maxButtons) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // 总页数较多时，采用省略号模式
    // 始终显示第一页和最后一页，中间区域显示 maxButtons - 2 个页码
    const middleButtons = maxButtons - 2; // 中间显示3个页码
    const half = Math.floor(middleButtons / 2);

    let start = Math.max(2, current - half);
    let end = Math.min(total - 1, start + middleButtons - 1);

    // 调整起始位置，确保中间区域显示足够的页码
    if (end - start + 1 < middleButtons) {
      start = Math.max(2, end - middleButtons + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, []);

  const visiblePages = getVisiblePages(totalPages, currentPage);

  // 处理页码切换
  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }

    onPageChange(page);

    // 滚动到指定位置
    if (scrollTargetSelector) {
      setTimeout(() => {
        const element = document.querySelector(scrollTargetSelector);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - scrollOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }
      }, 100);
    }
  }, [currentPage, totalPages, onPageChange, scrollTargetSelector, scrollOffset]);

  // 处理跳转输入
  const handleJumpInputChange = useCallback((e) => {
    const value = e.target.value;
    // 只允许输入数字
    if (value === '' || /^\d*$/.test(value)) {
      setJumpPage(value);
      setShowError(false); // 输入时隐藏错误提示
    }
  }, []);

  // 显示错误提示
  const showErrorTip = useCallback(() => {
    // 计算提示气泡位置
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 40, // 在输入框上方 40px
        left: rect.left + rect.width / 2 // 水平居中
      });
    }
    setShowError(true);
    // 2秒后自动隐藏错误提示
    setTimeout(() => {
      setShowError(false);
    }, 2000);
  }, []);

  // 处理跳转确认
  const handleJumpConfirm = useCallback(() => {
    const page = parseInt(jumpPage, 10);
    if (page && page >= 1 && page <= totalPages) {
      handlePageChange(page);
      setJumpPage('');
      setShowError(false);
    } else {
      // 输入无效，显示错误提示
      showErrorTip();
    }
  }, [jumpPage, totalPages, handlePageChange, showErrorTip]);

  // 处理回车键跳转
  const handleJumpKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleJumpConfirm();
    }
  }, [handleJumpConfirm]);

  // 如果只有一页，不显示分页器
  if (totalPages <= 1) {
    return null;
  }

  // 分页器内容
  const paginationContent = (
    <div className="pagination">
      {/* 上一页按钮 */}
      <button
        type="button"
        className="page-btn nav"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        上一页
      </button>

      {/* 总页数较少时：直接显示所有页码 */}
      {totalPages <= 5 && visiblePages.map((page) => (
        <button
          key={page}
          type="button"
          className={`page-btn ${page === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      ))}

      {/* 总页数较多时：使用省略号模式 */}
      {totalPages > 5 && (
        <>
          {/* 第一页 */}
          <button
            type="button"
            className={`page-btn ${currentPage === 1 ? 'active' : ''}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>

          {/* 前省略号 */}
          {visiblePages[0] > 2 && <span className="page-ellipsis">…</span>}

          {/* 中间页码 */}
          {visiblePages.map((page) => (
            <button
              key={page}
              type="button"
              className={`page-btn ${page === currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          {/* 后省略号 */}
          {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
            <span className="page-ellipsis">…</span>
          )}

          {/* 最后一页 */}
          <button
            type="button"
            className={`page-btn ${currentPage === totalPages ? 'active' : ''}`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      {/* 下一页按钮 */}
      <button
        type="button"
        className="page-btn nav"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        下一页
      </button>

      {/* 页码跳转 */}
      <div className="page-jump">
        <span className="page-jump-text">跳至</span>
        <input
          ref={inputRef}
          type="text"
          className={`page-jump-input ${showError ? 'error' : ''}`}
          value={jumpPage}
          onChange={handleJumpInputChange}
          onKeyDown={handleJumpKeyDown}
          placeholder=""
          aria-label="跳转页码"
          maxLength={String(totalPages).length}
        />
        <span className="page-jump-text">页</span>
        <button
          type="button"
          className="page-jump-btn"
          onClick={handleJumpConfirm}
        >
          确定
        </button>
      </div>
    </div>
  );

  // 使用 Portal 渲染错误提示气泡到 body，完全避免影响分页器布局
  const errorTooltip = showError && (
    <div
      className="page-error-tooltip"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`
      }}
    >
      请输入 1-{totalPages} 之间的页码
    </div>
  );

  return (
    <>
      {paginationContent}
      {errorTooltip && createPortal(errorTooltip, document.body)}
    </>
  );
};

export default Pagination;
